import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const spinnerVariants = cva('animate-spin motion-reduce:animate-none', {
  variants: {
    size: {
      sm: 'size-4',
      default: 'size-6',
      lg: 'size-8',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

export interface SpinnerProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'>,
    VariantProps<typeof spinnerVariants> {
  /** Announced by assistive tech. Pass '' when nearby text already says it. */
  label?: string;
}

/**
 * Inline counterpart to LoadingScreen, which stays separate because it owns the
 * whole viewport and the island class.
 */
const Spinner = React.forwardRef<HTMLSpanElement, SpinnerProps>(
  ({ size, label, className, ...props }, ref) => {
    const { t } = useTranslation();
    const announced = label ?? t('common.loading');

    return (
      <span
        ref={ref}
        role="status"
        className={cn('inline-flex items-center justify-center', className)}
        {...props}
      >
        <Loader2 className={spinnerVariants({ size })} strokeWidth={1.8} aria-hidden="true" />
        {announced ? <span className="sr-only">{announced}</span> : null}
      </span>
    );
  },
);
Spinner.displayName = 'Spinner';

export { Spinner, spinnerVariants };
